/**
 * Parts-mode enhance never stored a terminal article status. The spinner is
 * `enhanced === 'processing'`. The deadline applies only to a running write
 * that already has a start timestamp. Status null and enhanced pending are
 * not a stuck run. This decides success, failure, or keep waiting from
 * completion docs only — it does not touch article HTML.
 */

/**
 * Same limit as the server watchdog (PARTS_RUN_LIMIT_MS in
 * functions/src/OpenAI/enhanceRun.ts): the queue's retry backoff plus every
 * attempt. A shorter client deadline failed runs whose jobs were still
 * waiting for their next attempt.
 */
export const PARTS_GENERATE_DEADLINE_MS = 15 * 60 * 1000;

/** Same limit as WHOLE_RUN_LIMIT_MS on the server: both attempts of the whole-article job. */
export const WHOLE_GENERATE_DEADLINE_MS = 1170 * 1000;

export type PartValueSnap = {
    state?: string | null;
};

export type PartSnap = {
    part: string;
    count: number;
    parentState?: string | null;
    values: PartValueSnap[];
};

export type PartsTerminalInput = {
    enhanced: string | null | undefined;
    aiGenerateStatus?: string | null;
    aiGenerateMode?: string | null;
    now: number;
    startedAt?: number | null;
    parts: PartSnap[];
};

export type GenerationStatusPatch = {
    enhanced: 'done' | 'pending' | 'processing';
    aiGenerateStatus: 'done' | 'failed' | 'running';
};

export type PartsTerminalDecision =
    | {action: 'none'}
    | {action: 'success'; patch: GenerationStatusPatch}
    | {action: 'failed'; reason: 'part-failed' | 'deadline' | 'legacy'; patch: GenerationStatusPatch}
    | {action: 'wait'; waitMs: number; armClock: boolean};

const STATUS_KEYS: Array<keyof GenerationStatusPatch> = ['enhanced', 'aiGenerateStatus'];

/**
 * OpenAIArticleGenerate answered 409: another tab or click already runs the
 * generation. The Firebase SDK reads it as `functions/already-exists`.
 */
export function generationAlreadyRunning(error: unknown): boolean {
    return (error as {code?: unknown} | null | undefined)?.code === 'functions/already-exists';
}

export function partsSpinnerOn(enhanced: string | null | undefined): boolean {
    return enhanced === 'processing';
}

export function partsRetryVisible(article: {
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
}): boolean {
    return article.aiGenerateStatus === 'failed' && article.enhanced !== 'processing';
}

export function generationStatusPatch(decision: PartsTerminalDecision): GenerationStatusPatch | null {
    if (decision.action !== 'success' && decision.action !== 'failed') {
        return null;
    }
    const patch: GenerationStatusPatch = {
        enhanced: decision.patch.enhanced,
        aiGenerateStatus: decision.patch.aiGenerateStatus,
    };
    const extra = Object.keys(patch).filter((key) => !STATUS_KEYS.includes(key as keyof GenerationStatusPatch));
    if (extra.length) {
        throw new Error('generation status patch must not include article content');
    }
    return patch;
}

/** Same rule as the server commit: a part still being written is open even if one row failed. */
function classify(part: PartSnap): 'skip' | 'done' | 'failed' | 'open' {
    const values = part.values || [];
    if (part.parentState === 'failed') {
        return 'failed';
    }
    const count = part.count || 0;
    if (count <= 0 && values.length === 0) {
        return 'skip';
    }
    const expected = count > 0 ? count : values.length;
    const done = values.filter((value) => value.state === 'done').length;
    const pending = values.filter((value) => value.state !== 'done' && value.state !== 'failed').length;
    if (pending > 0) {
        return 'open';
    }
    if (values.some((value) => value.state === 'failed')) {
        return 'failed';
    }
    if (done >= expected && values.length >= expected) {
        return 'done';
    }
    return 'open';
}

function deadlineExceeded(clock: number, now: number): boolean {
    return clock > 0 && now - clock >= PARTS_GENERATE_DEADLINE_MS;
}

/** True when an open may settle parts status. Done and already-failed stay untouched. */
export function partsRunMaySettle(article: {
    enhanced?: string | null;
    aiGenerateMode?: string | null;
    aiGenerateStatus?: string | null;
} | null | undefined): boolean {
    if (!article || article.aiGenerateMode === 'whole') {
        return false;
    }
    if (article.enhanced !== 'processing' && article.enhanced !== 'pending') {
        return false;
    }
    if (article.enhanced === 'pending' && (article.aiGenerateStatus === 'failed' || article.aiGenerateStatus === 'done')) {
        return false;
    }
    return true;
}

/**
 * Finished articles and whole-article mode are not an in-flight parts run.
 * Pending with no completion docs has not started. Status null and enhanced
 * pending are never failed by this deadline. A running write uses its start
 * timestamp.
 */
export function resolvePartsTerminal(input: PartsTerminalInput): PartsTerminalDecision {
    if (!partsRunMaySettle(input)) {
        return {action: 'none'};
    }

    const kinds = input.parts.map(classify);
    const active = kinds.filter((kind) => kind !== 'skip');
    if (input.enhanced === 'pending' && active.length === 0 && !kinds.includes('failed')) {
        return {action: 'none'};
    }

    if (kinds.includes('failed') && !kinds.includes('open')) {
        return {
            action: 'failed',
            reason: 'part-failed',
            patch: {enhanced: 'pending', aiGenerateStatus: 'failed'},
        };
    }

    if (active.length > 0 && active.every((kind) => kind === 'done')) {
        return {
            action: 'success',
            patch: {enhanced: 'done', aiGenerateStatus: 'done'},
        };
    }

    // v1.14.2 wrote enhanced: processing and never a status; done lived only in
    // memory. Every run since writes running with processing, so processing
    // without a status is an enhance that ended long ago. Its rows have no
    // state and would keep the spinner forever.
    if (input.enhanced === 'processing' && !input.aiGenerateStatus) {
        return {
            action: 'failed',
            reason: 'legacy',
            patch: {enhanced: 'pending', aiGenerateStatus: 'failed'},
        };
    }

    // Status null and enhanced pending are not a stuck run. Only `running`
    // with a start timestamp may become failed after the limit.
    if (input.aiGenerateStatus !== 'running') {
        return {action: 'none'};
    }
    const startedAt = Number(input.startedAt || 0);
    if (deadlineExceeded(startedAt, input.now)) {
        return {
            action: 'failed',
            reason: 'deadline',
            patch: {enhanced: 'pending', aiGenerateStatus: 'failed'},
        };
    }

    // Completion `touch` is not a timestamp. A missing clock on a running
    // write is started by the caller; it is not treated as already overdue.
    return {
        action: 'wait',
        waitMs: startedAt
            ? Math.max(0, PARTS_GENERATE_DEADLINE_MS - (input.now - startedAt))
            : PARTS_GENERATE_DEADLINE_MS,
        armClock: !startedAt,
    };
}
